import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApi } from '../../../helpers/fetch';
import {
  setTopNavbar,
  clearTopNavbar,
} from '../../Reducers/actions/navbarActions';
import { fetchSubscribe, fetchUserSubscriptions } from '../../Reducers/actions/creationsActions';
import ViewCreationSkeleton from '../ViewCreation/ViewCreationSkeleton';
import Icon from '../../components/Icon';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import Unsubscribe from '../../components/Unsubscribe/Unsubscribe';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import { fromCentsToPounds } from '../../../helpers/money';
import { calculateReadingTime, formatTimeAgo, getFirstTitleFromHtml } from '../../../helpers/utilities';
import '../News/ViewNews.scss';
import './NewsAnalyticsDetail.scss';

const NEWS_STAT_CARDS = [
  { key: 'likes', label: 'Likes', icon: 'like_creation' },
  { key: 'views', label: 'Views', icon: 'eye_views' },
  { key: 'comments', label: 'Comments', icon: 'comment_analytics' },
  { key: 'favourites', label: 'Favourites', icon: 'star' },
  { key: 'shares', label: 'Shares', icon: 'share_android' },
];

const DUMMY_SUBSCRIBERS = [
  { user_id: 'd1', firstName: 'George', lastName: 'Carlin', username: 'George_Carlin', profilePicture: null },
  { user_id: 'd2', firstName: 'Louis', lastName: 'de Funès', username: 'Louis_de_Funes', profilePicture: null },
];

const DUMMY_CONTRIBUTORS = [
  { user_id: 'dc1', username: '@contrib1', amount: 5000 },
  { user_id: 'dc2', username: '@contrib2', amount: 1050 },
  { user_id: 'dc3', username: '@contrib3', amount: 1050 },
];

function NewsAnalyticsDetail() {
  const { newsId } = useParams();
  const dispatch = useDispatch();
  const auth_user_id = useSelector((state) => state.auth.user?.user_id);
  const auth_user = useSelector((state) => state.auth.user);
  const view_user = useSelector((state) => state.creation.view_user);

  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null);

  const fetchData = useCallback(async () => {
    if (!newsId) return;
    setLoading(true);
    setError(null);
    try {
      const [newsRes, analyticsRes] = await Promise.all([
        fetchApi(`/api/news/detail/${newsId}`),
        fetchApi(`/api/news/${newsId}/analytics`).catch(() => null),
      ]);

      const raw = newsRes?.success ? newsRes?.data : newsRes;
      const newsData = Array.isArray(raw) ? raw[0] : raw;
      setNews(newsData || null);

      if (analyticsRes?.success && analyticsRes?.data) {
        setAnalytics(analyticsRes.data);
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      setError(err?.message ?? 'Failed to load news analytics');
      setNews(null);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [newsId]);

  useEffect(() => {
    dispatch(
      setTopNavbar({
        title: 'news analytics',
        showBackArrow: true,
      })
    );
    fetchData();
    return () => dispatch(clearTopNavbar());
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (!auth_user_id) return;
    dispatch(fetchUserSubscriptions(auth_user_id));
  }, [auth_user_id, dispatch]);

  const readTimeMins = useMemo(() => {
    const mins = news?.content ? calculateReadingTime(news.content) : 0;
    return mins;
  }, [news?.content]);

  const totalReadTime = useMemo(() => {
    if (analytics?.totalReadTimeSeconds != null) {
      const sec = analytics.totalReadTimeSeconds;
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      if (h > 0) return `${h}h ${m}mins`;
      return `${m}mins`;
    }
    return '1h 23mins';
  }, [analytics]);

  const getStatValue = (key) => {
    if (analytics?.[key] != null) return analytics[key];
    if (!news) return 0;
    switch (key) {
      case 'likes':
        return news.total_likes ?? (Array.isArray(news.likes) ? news.likes.length : 0);
      case 'views':
        return news.views ?? news.view_count ?? 0;
      case 'comments':
        return news.total_comments ?? (Array.isArray(news.comments) ? news.comments.length : 0);
      case 'favourites':
        return news.saves_count ?? (Array.isArray(news.is_saved) ? news.is_saved.length : 0);
      case 'shares':
        return analytics?.shares ?? 0;
      default:
        return 0;
    }
  };

  const useDummyData = !analytics;
  const subscribers = analytics?.newSubscribers ?? (useDummyData ? DUMMY_SUBSCRIBERS : []);

  const creatorSubscriptions = auth_user?.subscriptions ?? view_user?.subscriptions ?? [];
  const getIsSubscribed = (sub) =>
    (creatorSubscriptions || []).some((s) => (s.user_id ?? s.id) === (sub.user_id ?? sub.id));

  const handleSubscribe = (subscribed_to_id, subscribe, sub) => {
    dispatch(fetchSubscribe(subscribed_to_id, subscribe));
    dispatch({
      type: 'SUBSCRIBE_SUCCESS',
      payload: { subscribe, subscriber_id: auth_user_id, subscribed_to_id, user: { ...sub, user_id: subscribed_to_id, is_subscribed: subscribe } },
    });
  };

  const handleUnsubscribeConfirm = () => {
    if (unsubscribeTarget) {
      handleSubscribe(unsubscribeTarget.user_id ?? unsubscribeTarget.id, false, unsubscribeTarget);
      setUnsubscribeTarget(null);
    }
  };

  const handleSubscribeClick = (sub) => {
    const isSubscribed = getIsSubscribed(sub);
    const subId = sub.user_id ?? sub.id;
    if (isSubscribed) {
      setUnsubscribeTarget(sub);
    } else {
      handleSubscribe(subId, true, sub);
    }
  };
  const contributors = analytics?.contributors ?? (useDummyData ? DUMMY_CONTRIBUTORS : []);
  const monthlyContributors = analytics?.monthlyContributors ?? (useDummyData ? DUMMY_CONTRIBUTORS.slice(0, 1) : []);

  const contributionsKpis = analytics?.contributionsKpis ?? (useDummyData ? {
    monthlyCount: 1,
    monthlyAmount: '€ 50.00',
    oneShotCount: 3,
    oneShotAmount: '€ 31.05',
  } : { monthlyCount: 0, monthlyAmount: '€ 0', oneShotCount: 0, oneShotAmount: '€ 0' });

  if (loading) return <ViewCreationSkeleton />;
  if (error) {
    return (
      <div className="news-analytics-detail news-analytics-detail__error">
        <p>{error}</p>
      </div>
    );
  }
  if (!news) {
    return (
      <div className="news-analytics-detail news-analytics-detail__error">
        <p>News not found</p>
      </div>
    );
  }

  const newsTitle = getFirstTitleFromHtml(news.content) || news.title || 'Untitled';
  const newsDate = formatTimeAgo(news.created_at);

  return (
    <div className="news-analytics-detail" role="main" aria-label="News analytics">
      <div className="new-container news-analytics-detail__view-section">
        <div className="thumbnail-news">
          {news.media_url ? (
            <img
              src={news.media_url}
              alt={newsTitle}
              className="img-thumbnail"
            />
          ) : (
            <div className="img-thumbnail news-analytics-detail__hero-placeholder" />
          )}
        </div>

        <div className="mx-16 mb-16">
          <p className="fs-md fg-dark my-16 fw-medium-bold">{newsTitle}</p>

          <div className="d-flex aic fs-real fw-medium fg-grey7">
            <span>{readTimeMins} {readTimeMins === 1 ? 'min' : 'mins'} read</span>
            <span className="dot" />
            <span>{newsDate}</span>
          </div>
        </div>
      </div>

      <div className="news-analytics-detail__content">
        <div className="news-analytics-detail__stats">
          {NEWS_STAT_CARDS.map(({ key, label, icon }) => (
            <div key={key} className="news-analytics-detail__stat-card">
              <Icon alias={icon} width={32} height={32} color="#FF8CDC" />
              <span className="news-analytics-detail__stat-value">
                {getStatValue(key).toLocaleString()}
              </span>
              <span className="news-analytics-detail__stat-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="news-analytics-detail__total-read">
          <span className="news-analytics-detail__total-read-label">Total read time</span>
          <span className="news-analytics-detail__total-read-value">{totalReadTime}</span>
        </div>

        {subscribers.length > 0 && (
          <section className="news-analytics-detail__section">
            <div className="news-analytics-detail__section-header">
              <h2>New subscribers</h2>
              <span>{subscribers.length} subscribers</span>
            </div>
            <ul className="news-analytics-detail__list">
              {subscribers.slice(0, 3).map((sub) => {
                const isSubscribed = getIsSubscribed(sub);
                const subId = sub.user_id ?? sub.id;
                return (
                  <li key={subId} className="news-analytics-detail__account-row">
                    <div className="news-analytics-detail__avatar">
                      <ProfilePicture
                        imgUrl={sub.profile_picture || sub.avatar || sub.profilePicture}
                        no_edit
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="news-analytics-detail__account-info">
                      <span className="news-analytics-detail__account-name">
                        {sub.firstName} {sub.lastName}
                      </span>
                      <span className="news-analytics-detail__account-handle">
                        {sub.username || sub.handle}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={`news-analytics-detail__subscribe-btn ${isSubscribed ? 'news-analytics-detail__subscribe-btn--subscribed' : ''}`}
                      onClick={() => handleSubscribeClick(sub)}
                      aria-label={isSubscribed ? 'Unsubscribe' : 'Subscribe'}
                    >
                      {isSubscribed ? 'Subscribed' : 'Subscribe'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {unsubscribeTarget && (
          <Unsubscribe
            user={{
              user_id: unsubscribeTarget.user_id ?? unsubscribeTarget.id,
              userName: unsubscribeTarget.username ?? unsubscribeTarget.handle ?? getUserDisplayName(unsubscribeTarget),
              profilePicture: unsubscribeTarget.profile_picture ?? unsubscribeTarget.profilePicture ?? unsubscribeTarget.avatar,
              ...unsubscribeTarget,
            }}
            onClose={() => setUnsubscribeTarget(null)}
            onSubmit={handleUnsubscribeConfirm}
          />
        )}

        <section className="news-analytics-detail__section">
          <div className="news-analytics-detail__section-header">
            <h2>Contributions</h2>
            <span>
              {(monthlyContributors.length + contributors.length)} contributors
            </span>
          </div>
          <div className="news-analytics-detail__kpi-grid">
            <div className="news-analytics-detail__kpi-card">
              <span className="news-analytics-detail__kpi-label">Monthly contributors</span>
              <span className="news-analytics-detail__kpi-value">
                {contributionsKpis.monthlyCount ?? monthlyContributors.length}
              </span>
            </div>
            <div className="news-analytics-detail__kpi-card">
              <span className="news-analytics-detail__kpi-label">Monthly amount</span>
              <span className="news-analytics-detail__kpi-value">
                {contributionsKpis.monthlyAmount ?? fromCentsToPounds(0)}
              </span>
            </div>
          </div>
          {monthlyContributors.length > 0 && (
            <div className="news-analytics-detail__table">
              <div className="news-analytics-detail__table-header">
                <span>Monthly contributors</span>
                <span>Amount</span>
              </div>
              {monthlyContributors.map((c) => (
                <div key={c.user_id || c.id} className="news-analytics-detail__table-row">
                  <span>{c.username || c.name || 'Contributor'}</span>
                  <span>{typeof c.amount === 'number' ? fromCentsToPounds(c.amount) : (c.amount ?? fromCentsToPounds(0))}</span>
                </div>
              ))}
            </div>
          )}
          <div className="news-analytics-detail__kpi-grid">
            <div className="news-analytics-detail__kpi-card">
              <span className="news-analytics-detail__kpi-label">Contributors</span>
              <span className="news-analytics-detail__kpi-value">
                {contributionsKpis.oneShotCount ?? contributors.length}
              </span>
            </div>
            <div className="news-analytics-detail__kpi-card">
              <span className="news-analytics-detail__kpi-label">Total amount</span>
              <span className="news-analytics-detail__kpi-value">
                {contributionsKpis.oneShotAmount ?? fromCentsToPounds(0)}
              </span>
            </div>
          </div>
          <div className="news-analytics-detail__table">
            <div className="news-analytics-detail__table-header">
              <span>Contributors</span>
              <span>Amount</span>
            </div>
            {contributors.length > 0 ? (
              contributors.map((c) => (
                <div key={c.user_id || c.id} className="news-analytics-detail__table-row">
                  <span>{c.username || c.name || 'Contributor'}</span>
                  <span>{typeof c.amount === 'number' ? fromCentsToPounds(c.amount) : (c.amount ?? fromCentsToPounds(0))}</span>
                </div>
              ))
            ) : (
              <p className="news-analytics-detail__empty">No contributors yet</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default NewsAnalyticsDetail;
